import React, {useEffect, useState} from 'react';
import { useTheme } from '@mui/material/styles';
import {Autocomplete, Box, Button, Card, debounce, FormControl, Grid2 as Grid, InputAdornment, Link, Stack, TextField, Typography} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import SearchIcon from '@mui/icons-material/Search';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';

import Container from '../../../components/Container';
import ShortFiler from '../../../types/filers/shortFiler';
import CountUp from 'react-countup';
import VisibilitySensor from 'react-visibility-sensor';
import { useErrorBoundary } from 'react-error-boundary';

const stats = [
  {
    count: 1000,
    type: "contributions",
    subtitle:
      'to candidates and committees.',
  },
  {
    count: 1000,
    type: "expenditures",
    subtitle:
      'made by Massachusetts political committees.',
  },
  {
    count: 1000,
    type: "reports",
    subtitle: 'filed with OCPF by candidates and committees.',
  },
];

const DataStats = (): JSX.Element => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });
  const { showBoundary } = useErrorBoundary();

  useEffect(() => {
    getYTDStats();
  }, []);

  const [viewPortEntered, setViewPortEntered] = useState(false);

  const [contributionOpen, setContributionOpen] = useState(false);
  const [expenditureOpen, setExpenditureOpen] = useState(false);
  const [filerOptions, setFilerOptions] = useState<readonly ShortFiler[]>([]);
  const [selectedFiler, setSelectedFilters] = useState<ShortFiler>();
  const [showContributionError, setShowContributionError] = useState(false);
  const [contributionErrorMessage, setContributionErrorMessage] = useState("");
  const [showExpenditureError, setShowExpenditureError] = useState(false);
  const [expenditureErrorMessage, setExpenditureErrorMessage] = useState("");
  const [contributionCount, setCountributionCount] = useState(1000);

  const [ytdStats, setYtdStats] = useState<typeof stats>(stats);

  const getYTDStats = async () => {

    try{
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/data/ytdTotals`)
      .then(result => {
        if (!result.ok) {
          showBoundary(new Error('Failed to get YTD data summary.'));
        }
        return result.json();
      })
      .then(result => {
        setCountributionCount(result.contributionCount);
        stats[0].count = result.contributionCount;
        stats[1].count = result.expenditureCount;
        stats[2].count = result.reportCount;
        setYtdStats(stats);
      });
    }catch(error){
        console.error('Uncaught Error:', error);
    }
  } 

  const setViewPortVisibility = (isVisible: boolean) => {
    if (viewPortEntered) {
      return;
    }

    setViewPortEntered(isVisible);
  };

  const handleContributionOpen = () => {
    setContributionOpen(true);
  };

  const handleContributionClose = () => {
    setContributionOpen(false);
    setFilerOptions([]);
  };

  const handleExpenditureOpen = () => {
    setExpenditureOpen(true);
  };

  const handleExpenditureClose = () => {
    setExpenditureOpen(false);
    setFilerOptions([]);
  };

  const filterByName = debounce((event: any) => {
    let searchInput = event.target.value.toLowerCase();
    (async () => {
      refreshFilerList(searchInput);
    })();
  }, 800);

  const refreshFilerList = async (searchInput: string) => {
    setShowContributionError(false);
    setContributionErrorMessage("");
    setShowExpenditureError(false);
    setExpenditureErrorMessage("");
    
    try{
      if(searchInput){
        let url = `${process.env.REACT_APP_API_BASE_URL}/filers/listings/A?searchPhrase=${searchInput}`;
        await fetch(url)
        .then(result => result.json())
        .then(result => {
          setFilerOptions(result);
        });
      }
      else{
        setFilerOptions([]);
      }

    }catch(error){
      console.error('Uncaught Error:', error);
    }
  }


  const updateSelectedFiler = (filer: ShortFiler) => {
    setSelectedFilters(filer);
  }

  const clearSelectedFiler = () => {
    setSelectedFilters(undefined);
  }

  const searchFilerContributions = () => {
    if(selectedFiler && selectedFiler.cpfId){
      window.location.href = `/filers?q=${selectedFiler.cpfId}&section=Contributions`;
    }
    else{
      setShowContributionError(true);
      setContributionErrorMessage("Please select a filer to search.");
    }
  }

  const searchFilerExpenditures = () => {
    if(selectedFiler && selectedFiler.cpfId){
      window.location.href = `/filers?q=${selectedFiler.cpfId}&section=Expenditures`;
    }
    else{
      setShowExpenditureError(true);
      setExpenditureErrorMessage("Please select a filer to search.");
    }
  }

  return (
    <Container>
      <Grid container spacing={4} direction={isMd ? 'row' : 'column'}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Box marginBottom={4}>
            <Typography sx={{ fontWeight: 700 }} variant={'h5'} gutterBottom>
              {new Date().getFullYear()} YTD Campaign Finance Data Summary
            </Typography>
            <Typography variant={'h6'} component={'p'} color={'text.secondary'}>
              Track every dollar raised and spent in Massachusetts elections.
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {
            ytdStats?.map((item, i) => (
              <Grid size={{ xs: 12, md: 6 }} key={i}>
                <Typography variant="h4" color={'primary'} gutterBottom>
                  <VisibilitySensor
                    onChange={(isVisible: boolean) => setViewPortVisibility(isVisible)}
                    delayedCall>
                    <CountUp
                      duration={1}
                      end={viewPortEntered ? item.count: 0}
                      start={0}
                    />
                  </VisibilitySensor>
                  <Typography padding={1}  fontWeight={500} color={'text.primary'} component="span"><i>{item.type}</i></Typography>
                </Typography>
                <Typography color="text.secondary" component="p">
                  {item.subtitle}
                </Typography>
              </Grid>
            ))
            }
          </Grid>
          <Box marginTop={3} display={'flex'} justifyContent={'left'}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              component={Link}
              href="/Data/SearchItems"
              endIcon={<ArrowRightAltIcon />}
            >
              Search Campaign Finance Database
            </Button>
          </Box>
        </Grid>
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          size={{ xs: 12, md: 6 }}
          sx={{ display: 'flex',}}
        >
          <Grid size={12}>
          <Box
            padding={{ xs: 3, sm: 6 }}
            width={1}
            component={Card}
            boxShadow={4}
            data-aos="fade-up"
          >
            <Box
              display={'flex'}
              justifyContent={'space-between'}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              flexDirection={{ xs: 'column', sm: 'row' }}
            >
              <Box>
                <Typography fontWeight={700} variant={'h6'} gutterBottom paddingBottom={0.5}>
                    Contributions Received
                </Typography>
                <FormControl variant='standard'>
                  <Autocomplete
                    filterOptions={(x) => x}
                    sx={{ width: 320 }}
                    onChange={(event: any, newValue: ShortFiler | null) => {
                      if (newValue) {
                          updateSelectedFiler(newValue);
                      }
                      else{
                          clearSelectedFiler();
                      }
                    }}
                    open={contributionOpen}
                    onOpen={handleContributionOpen}
                    onClose={handleContributionClose}
                    isOptionEqualToValue={(option, value) => option.cpfId === value.cpfId}
                    getOptionLabel={(option) => option.filerName}
                    renderOption={(props, option) => 
                      (
                        <Stack 
                          key={option.cpfId}
                          direction="row" 
                          display={"flex"} 
                          justifyContent={"flex-start"}
                        >
                          <Stack>
                            {/*placeholder for filer photos*/}                        
                          </Stack>
                          <Stack>
                              <Typography variant='body2' {...props}>
                                {option.cpfId} {option.filerName}
                              </Typography>
                              {
                                option.isCandidate && 
                                <Typography variant='body2'  component={"i"} color='text.secondary' paddingLeft={2}>
                                  {option.officeSought}
                                </Typography>
                              }
                          </Stack>
                        </Stack>
                        )
                    }
                    options={filerOptions}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant='standard'
                        placeholder={"Type filer name or ID..."}
                        
                        title='type filer name or ID to search for contributions'
                        onChange={filterByName}
                        error ={showContributionError}
                        helperText={contributionErrorMessage}
                        slotProps={{
                          input: {
                            'aria-label': 'type filer name or ID to search for contributions',
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon color="primary" />
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    )}
                  />
                </FormControl>
              </Box>
              <Box display="flex" marginTop={{ xs: 2, md: 0 }}>
                <Button onClick={searchFilerContributions} variant="contained" color="primary" size="large">
                  Search
                </Button>
              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid size={12}>
          <Box
            padding={{ xs: 3, sm: 6 }}
            width={1}
            component={Card}
            boxShadow={4}
            data-aos="fade-up"
          >
            <Box
              display={'flex'}
              justifyContent={'space-between'}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              flexDirection={{ xs: 'column', sm: 'row' }}
            >
              <Box>
                <Typography fontWeight={700} variant={'h6'} gutterBottom>
                    Expenditures Made
                </Typography>
                <FormControl variant="standard" sx={{ marginRight:3, p:0, width: 300 }}>  
                <Autocomplete
                    filterOptions={(x) => x}
                    sx={{ width: 320 }}
                    onChange={(event: any, newValue: ShortFiler | null) => {
                      if (newValue) {
                          updateSelectedFiler(newValue);
                      }
                      else{
                          clearSelectedFiler();
                      }
                    }}
                    open={expenditureOpen}
                    onOpen={handleExpenditureOpen}
                    onClose={handleExpenditureClose}
                    isOptionEqualToValue={(option, value) => option.cpfId === value.cpfId}
                    getOptionLabel={(option) => option.filerName}
                    renderOption={(props, option) => 
                      (
                        <Stack 
                          key={option.cpfId}
                          direction="row" 
                          display={"flex"} 
                          justifyContent={"flex-start"}
                        >
                          <Stack>
                            {/*placeholder for filer photos*/}                        
                          </Stack>
                          <Stack>
                              <Typography variant='body2' {...props}>
                                {option.cpfId} {option.filerName}
                              </Typography>
                              {
                                option.isCandidate && 
                                <Typography variant='body2'  component={"i"} color='text.secondary' paddingLeft={2}>
                                  {option.officeSought}
                                </Typography>
                              }
                          </Stack>
                        </Stack>
                        )
                    }
                    options={filerOptions}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant='standard'
                        placeholder={"Type filer name or ID..."}
                        title='type filer name or ID to search for expenditures'
                        //aria-label='type filer name or ID to search for expenditures'
                        onChange={filterByName}
                        error = {showExpenditureError}
                        helperText={expenditureErrorMessage}
                        slotProps={{
                          input: {
                            'aria-label': 'type filer name or ID to search for expenditures',
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon color="primary" />
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    )}
                  />
                </FormControl>
              </Box>
              <Box display="flex" marginTop={{ xs: 2, md: 0 }}>
                <Button onClick={searchFilerExpenditures} variant="contained" color="secondary" size="large">
                  Search
                </Button>
              </Box>
            </Box>
          </Box>
        </Grid>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DataStats;
